import { useRequest } from 'ahooks';
import React, { useCallback, useEffect, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import LoadingImage from '../../components/general/LoadingImage';
import { uiHelpers } from '../../helpers';
import { bsTiny, capitalizeFirstLetter } from '../../helpers/ui.helper';
import { CustomerFacingSubscription } from '../../py-generated/datalouder/membership/types';
import { transactionService } from '../../services';
import ExtendSubscriptionView from './ExtendSubscriptionView';

const Styles = styled.div`
  margin: 0 -1rem;
  display: flex;
  align-items: center;

  & > .subscription-status {
    text-align: center;
    width: 5rem;
    margin-right: 0;
  }
`;

const canCancel = (status: string) => {
  return ['ACTIVE', 'SUSPENDED', 'TRIALING', 'PAST_DUE', 'UNPAID'].includes(
    status.toUpperCase()
  );
};

const canExtend = (provider: string, status: string) => {
  return provider === 'stripe' && ['active', 'trialing'].includes(status);
};

interface Props {
  subscription: CustomerFacingSubscription;
  onCancelled: () => void;
}

const SubscriptionDetailView: React.FC<Props> = ({
  subscription,
  onCancelled,
}) => {
  const [t] = useTranslation();
  const [extendModalVisible, showExtendModal] = useState(false);
  const [approvalModalVisible, showApprovalModal] = useState(false);

  useEffect(() => {
    if (subscription.approval_link) {
      showApprovalModal(true);
    }
  }, [subscription.approval_link]);

  const subscriptionId = subscription.subscription_id;

  const { run: cancel, loading: cancelling } = useRequest(
    // the general cancelSubscriptionOptions() only support stripe
    // for paypal, the old cancelPaypalSubscriptionOptions is used.
    subscription.provider === 'stripe'
      ? transactionService.cancelSubscriptionOptions(
          subscription.provider,
          subscriptionId
        )
      : transactionService.cancelPaypalSubscriptionOptions(subscriptionId),
    {
      manual: true,
      onSuccess: () => {
        alert(t('subscription-cancelled'));
        onCancelled();
      },
    }
  );

  const cancelSubscription = useCallback(() => {
    if (confirm(t('confirm-cancel-subscription'))) {
      cancel();
    }
  }, [cancel, t]);

  return (
    <Styles>
      <div className="subscription-status">
        <b>
          <small>
            {t('subscription-status-' + subscription.status.toUpperCase())}
          </small>
        </b>
      </div>
      <div>
        <div>
          <Badge bg="primary">
            {capitalizeFirstLetter(subscription.provider)}
          </Badge>{' '}
          <small>{subscription.subscription_id}</small>
        </div>
        <div>
          {subscription.estimated_next_bill_time && (
            <small>
              {t('next-bill-date')}
              {uiHelpers.formatDateLocalFromTimestamp(
                subscription.estimated_next_bill_time
              )}
              {subscription.renew_price && (
                <>
                  <br />
                  HKD {subscription.renew_price}
                </>
              )}
            </small>
          )}
        </div>
        {canExtend(subscription.provider, subscription.status) && (
          <>
            <Button
              size={bsTiny}
              variant="success"
              onClick={() => showExtendModal(true)}
            >
              {t('extend-subscription')}
            </Button>{' '}
          </>
        )}
        {canCancel(subscription.status) && (
          <>
            <Button
              size={bsTiny}
              variant="danger"
              onClick={cancelSubscription}
              disabled={cancelling}
            >
              {t('cancel-subscription')}{' '}
              <LoadingImage small loading={cancelling} />
            </Button>{' '}
          </>
        )}
        {subscription.approval_link && (
          <Button
            size={bsTiny}
            variant="success"
            href={subscription.approval_link}
            target="_blank"
          >
            {t('Approve')}
          </Button>
        )}
      </div>

      {extendModalVisible && (
        <Modal
          show={extendModalVisible}
          size="xl"
          fullscreen="lg-down"
          centered
          onHide={() => showExtendModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t('extend-subscription')} ({t('one-time-payment')})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ExtendSubscriptionView subscription={subscription} />
          </Modal.Body>
        </Modal>
      )}
      {approvalModalVisible && (
        <Modal
          show={approvalModalVisible}
          size="sm"
          centered
          onHide={() => showApprovalModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('approve-subscription')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t('approve-subscription-reason')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() =>
                (window.location.href = subscription.approval_link)
              }
            >
              {t('approve-subscription-button')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Styles>
  );
};

export default SubscriptionDetailView;
